import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ProtectedCourse from "./../ProtectedCourse";
import "./Navbar.css";
import "./../AccountMenu.css";
import { BACKEND_URL } from "./../backendconfig";

const backend = BACKEND_URL.replace(/\/+$/, "");
const menuItems = [
  { label: "Home", to: "/" },

  { label: "Free Demo", to: "/#free-demo-intro" },
  { label: "Courses", to: "/#courses" },
  { label: "Embroidery Machines", to: "/#embroidery-videos" },
  { label: "About Us", to: "/#about" },
  { label: "Why Choose Us", to: "/#why-us" },
  { label: "Videos", to: "/#videos" },
  { label: "Gallery", to: "/gallery" },
  { label: "Student Success", to: "/#designers" },
  { label: "FAQ", to: "/#faq" },
  { label: "Contact Us", to: "/#contact" },
];

export default function Navbar() {
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);
  const accountButton = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [authMode, setAuthMode] = useState(null);

  const { pathname, hash, key } = useLocation();

  useEffect(() => {
    function closeOutside(event) {
      const target = event.target;

      if (!(target instanceof Element)) return;

      // Keep the submenu open while touching or scrolling
      // anywhere inside the open mobile navigation.
      const insideMobileMenu =
        window.matchMedia("(max-width: 1199px)").matches &&
        target.closest("#ltc-nav-menu.ltc-nav-menu-open");

      if (insideMobileMenu) return;

      if (accountRef.current && !accountRef.current.contains(target)) {
        setAccountOpen(false);
      }
    }
    function closeEscape(event) {
      if (event.key === "Escape" && accountOpen) {
        setAccountOpen(false);
        accountButton.current?.focus();
      }
    }
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeEscape);
    };
  }, [accountOpen]);
  useEffect(() => {
    setAccountOpen(false);
  }, [pathname, hash, key, menuOpen]);

  const closeAuthPopup = useCallback(() => {
    setAuthMode(null);
  }, []);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const [authError, setAuthError] = useState("");
  const sessionRequest = useRef(0);
  const refreshSession = useCallback(async () => {
    const requestId = ++sessionRequest.current;
    setAuthError("");

    try {
      const response = await fetch(`${backend}/users.php?action=session`, {
        credentials: "include",
        cache: "no-store",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Unable to check login.");
      }

      // Ignore responses from requests started before a newer auth update.
      if (requestId !== sessionRequest.current) return;

      if (data.authenticated === true && !data.user) {
        throw new Error("Session response is missing user details.");
      }

      setUser(data.authenticated === true ? data.user : null);
    } catch (error) {
      if (requestId === sessionRequest.current) {
        setAuthError(error.message || "Unable to check login.");
      }
    } finally {
      if (requestId === sessionRequest.current) {
        setAuthLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession, pathname, key]);

  // Also refresh when login happens through a protected course popup.
  useEffect(() => {
    function handleAuthChange(event) {
      const auth = event.detail;

      if (auth?.authenticated === true && auth.user) {
        // Invalidate any session check still running.
        sessionRequest.current += 1;

        setUser(auth.user);
        setAuthLoading(false);
        setAuthError("");
        setAuthMode(null);
        return;
      }
      refreshSession();
    }

    window.addEventListener("ltc-auth-changed", handleAuthChange);

    return () => {
      window.removeEventListener("ltc-auth-changed", handleAuthChange);
    };
  }, [refreshSession]);

  const handleAuthenticated = useCallback(() => {
    setAuthMode(null);
  }, []);

  async function handleLogout() {
    if (loggingOut) return;

    const confirmed = window.confirm("Are you sure you want to log out?");

    if (!confirmed) return;

    setLoggingOut(true);
    setAuthError("");

    try {
      const response = await fetch(`${backend}/users.php?action=logout`, {
        method: "POST",
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok || data.success !== true) {
        throw new Error(data.message || "Unable to log out.");
      }

      setUser(null);
      setMenuOpen(false);

      window.location.reload();
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setLoggingOut(false);
    }
  }
  function openAuthPopup(mode) {
    setMenuOpen(false);
    setAuthMode(mode);
  }

  useEffect(() => {
    setMenuOpen(false);
    setAuthMode(null);
  }, [pathname, hash, key]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setAuthMode(null);
      }
    };

    const handleResize = () => {
      if (window.innerWidth >= 1200) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const isActive = (item) => {
    if (item.to === "/") {
      return pathname === "/" && !hash;
    }

    if (item.to.startsWith("/#")) {
      return pathname === "/" && hash === item.to.slice(1);
    }

    return pathname === item.to;
  };

  const handleNavigation = (event, item) => {
    setMenuOpen(false);

    // Preserve Ctrl/Cmd-click and opening links in a new tab.
    if (
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    if (item.to === "/" && pathname === "/" && !hash) {
      event.preventDefault();

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return;
    }

    // Scroll again when selecting the current section.
    if (
      item.to.startsWith("/#") &&
      pathname === "/" &&
      hash === item.to.slice(1)
    ) {
      let sectionId;

      try {
        sectionId = decodeURIComponent(item.to.split("#")[1]);
      } catch {
        return;
      }

      const section = document.getElementById(sectionId);

      if (section) {
        event.preventDefault();

        section.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  };

  return (
    <>
      <header className="ltc-nav-header">
        <div className="ltc-nav-container">
          <Link
            to="/"
            className="ltc-nav-brand"
            aria-label="LifeTime Embroidery Design Class Home"
            onClick={(event) =>
              handleNavigation(event, { label: "Home", to: "/" })
            }
          >
            <span className="ltc-nav-logo-frame">
              <img src="/logo.jpg" alt="" className="ltc-nav-logo-image" />
            </span>

            <span className="ltc-nav-brand-copy">
              <span className="ltc-nav-brand-title">
                Life
                <span className="ltc-nav-brand-accent">Time</span>
              </span>

              <span className="ltc-nav-brand-subtitle">
                Embroidery Design Class
              </span>
            </span>
          </Link>

          <button
            type="button"
            className={`ltc-nav-toggle ${
              menuOpen ? "ltc-nav-toggle-open" : ""
            }`}
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={menuOpen}
            aria-controls="ltc-nav-menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="ltc-nav-toggle-line" />
            <span className="ltc-nav-toggle-line" />
            <span className="ltc-nav-toggle-line" />
          </button>

          <nav
            id="ltc-nav-menu"
            className={`ltc-nav-menu ${menuOpen ? "ltc-nav-menu-open" : ""}`}
            aria-label="Main navigation"
          >
            <ul className="ltc-nav-list">
              {menuItems.map((item) => {
                const active = isActive(item);

                return (
                  <li key={item.to} className="ltc-nav-item">
                    <Link
                      to={item.to}
                      className={`ltc-nav-link ${
                        active ? "ltc-nav-link-active" : ""
                      }`}
                      aria-current={active ? "location" : undefined}
                      onClick={(event) => handleNavigation(event, item)}
                    >
                      {item.label}
                    </Link>
                  </li>
                );
              })}

              {/* <li className="ltc-nav-item ltc-nav-auth-actions">
                <button
                  type="button"
                  className="ltc-nav-login"
                  aria-haspopup="dialog"
                  onClick={() => openAuthPopup("login")}
                >
                  Login
                </button>

                <button
                  type="button"
                  className="ltc-nav-signup"
                  aria-haspopup="dialog"
                  onClick={() => openAuthPopup("register")}
                >
                  Sign Up
                </button>
              </li>
               */}

              <li className="ltc-nav-item ltc-nav-auth-actions">
                {authLoading ? (
                  <span role="status">Checking login…</span>
                ) : user ? (
                  <div className="ltc-account-menu" ref={accountRef}>
                    <button
                      type="button"
                      ref={accountButton}
                      className="ltc-account-trigger"
                      aria-expanded={accountOpen}
                      aria-controls="ltc-account-links"
                      onClick={() => setAccountOpen((value) => !value)}
                    >
                      <span className="ltc-account-avatar" aria-hidden="true">
                        {(user.name?.trim() || "U").slice(0, 1).toUpperCase()}
                      </span>
                      <span>
                        {user.name?.trim().split(/\s+/)[0] || "Account"}
                      </span>
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </button>
                    {accountOpen && (
                      <div
                        id="ltc-account-links"
                        className="ltc-account-dropdown"
                      >
                        <Link
                          to="/profile"
                          onClick={() => {
                            setAccountOpen(false);
                            setMenuOpen(false);
                          }}
                        >
                          Profile
                        </Link>
                        <Link
                          to="/change-password"
                          onClick={() => {
                            setAccountOpen(false);
                            setMenuOpen(false);
                          }}
                        >
                          Change Password
                        </Link>
                        <button
                          type="button"
                          onClick={handleLogout}
                          disabled={loggingOut}
                        >
                          {loggingOut ? "Logging out…" : "Logout"}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className="ltc-nav-login"
                      aria-haspopup="dialog"
                      onClick={() => openAuthPopup("login")}
                    >
                      Login
                    </button>

                    <button
                      type="button"
                      className="ltc-nav-signup"
                      aria-haspopup="dialog"
                      onClick={() => openAuthPopup("register")}
                    >
                      Sign Up
                    </button>
                  </>
                )}

                {authError && (
                  <span className="ltc-nav-auth-error" role="alert">
                    {authError}
                  </span>
                )}
              </li>
            </ul>
          </nav>
        </div>
      </header>

      {authMode && (
        <ProtectedCourse
          key={authMode}
          initialMode={authMode}
          onClose={closeAuthPopup}
          onAuthenticated={handleAuthenticated}
        >
          {null}
        </ProtectedCourse>
      )}
    </>
  );
}
